/**
 * Issues Overview Widget — Aggregated issue stats for Control Hub
 *
 * Shows summary badges (total, open, critical, blocking),
 * severity distribution bars, and recent issues list.
 */

import { useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { AlertCircle, CheckCircle, Clock } from '@untitledui/icons';
import { useIssuesDashboard, type RecentIssue } from '@/hooks/api/use-issues-dashboard';
import { Badge } from '@/components/base/badges/badges';

const SEVERITY_CONFIG: Record<string, { color: string; badgeColor: 'error' | 'warning' | 'brand' | 'success'; label: string }> = {
    critical: { color: '#7A271A', badgeColor: 'error', label: 'Critical' },
    high: { color: '#F04438', badgeColor: 'error', label: 'High' },
    medium: { color: '#F79009', badgeColor: 'warning', label: 'Medium' },
    low: { color: '#17B26A', badgeColor: 'success', label: 'Low' },
};

function getRelativeTime(dateStr: string | null): string {
    if (!dateStr) return '-';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
}

interface StatBadgeProps {
    label: string;
    value: number;
    variant: 'default' | 'warning' | 'error';
}

function StatBadge({ label, value, variant }: StatBadgeProps) {
    const borderClass = variant === 'error'
        ? 'border-l-error-500'
        : variant === 'warning'
            ? 'border-l-warning-500'
            : 'border-l-tertiary';

    return (
        <div className={`border-l-2 ${borderClass} pl-3 py-1`}>
            <p className="text-xs font-medium text-tertiary">{label}</p>
            <p className="text-lg font-semibold text-primary">{value}</p>
        </div>
    );
}

interface IssuesOverviewProps {
    startDate?: string | null;
    endDate?: string | null;
}

export function IssuesOverview({ startDate, endDate }: IssuesOverviewProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { data, isLoading } = useIssuesDashboard(startDate, endDate);

    if (isLoading) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="animate-pulse flex items-center gap-2">
                    <div className="h-5 w-40 bg-tertiary/20 rounded" />
                </div>
            </div>
        );
    }

    if (!data) return null;

    const { summary, by_severity, recent_issues } = data;
    const totalActive = summary.open_issues;

    // Build severity bars data
    const severityEntries = Object.entries(SEVERITY_CONFIG)
        .map(([key, config]) => ({
            key,
            ...config,
            value: by_severity[key] ?? 0,
        }));

    const maxSeverityValue = Math.max(1, ...severityEntries.map(e => e.value));

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <AlertCircle className="size-5 text-warning-600" />
                    <h3 className="text-lg font-semibold text-primary">Issues Overview</h3>
                </div>
                <a
                    onClick={() => navigate('/shipper/containers?has_active_alerts=true', { state: navigationStateFrom(location) })}
                    className="text-sm font-medium text-tertiary hover:text-primary cursor-pointer transition-colors"
                >
                    View all issues &rarr;
                </a>
            </div>

            {/* Summary badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <StatBadge label="Total Issues" value={summary.total_issues} variant="default" />
                <StatBadge label="Open" value={summary.open_issues} variant="warning" />
                <StatBadge label="Critical" value={summary.critical_issues} variant="error" />
                <StatBadge label="Blocking" value={summary.blocking_issues} variant="error" />
            </div>

            {/* Severity distribution */}
            {totalActive > 0 && (
                <div className="mb-5">
                    <p className="text-sm font-medium text-tertiary mb-3">Severity (Active Issues)</p>
                    <div className="space-y-2">
                        {severityEntries.map(entry => (
                            <div key={entry.key} className="flex items-center gap-3">
                                <span className="text-xs font-medium text-tertiary w-14">{entry.label}</span>
                                <div className="flex-1 bg-secondary rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full transition-all"
                                        style={{
                                            width: `${(entry.value / maxSeverityValue) * 100}%`,
                                            backgroundColor: entry.color,
                                        }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-primary w-6 text-right">{entry.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recent issues */}
            {recent_issues.length > 0 && (
                <div>
                    <p className="text-sm font-medium text-tertiary mb-3">Recent Issues</p>
                    <div className="space-y-2">
                        {recent_issues.slice(0, 5).map((issue: RecentIssue) => {
                            const severityConfig = SEVERITY_CONFIG[issue.severity];
                            const isResolved = ['resolved', 'false_positive', 'wont_fix'].includes(issue.status);
                            return (
                                <div
                                    key={issue.issue_id}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`View issue: ${issue.title}`}
                                    onClick={() => {
                                        if (issue.container_id) {
                                            navigate(`/shipper/containers/${issue.container_id}?tab=issues`, {
                                                state: navigationStateFrom(location),
                                            });
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && issue.container_id) {
                                            navigate(`/shipper/containers/${issue.container_id}?tab=issues`, {
                                                state: navigationStateFrom(location),
                                            });
                                        }
                                    }}
                                    className={`flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer ${isResolved ? 'opacity-50' : ''}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm truncate ${isResolved ? 'text-tertiary line-through' : 'text-primary'}`}>{issue.title}</p>
                                    </div>
                                    {isResolved ? (
                                        <Badge color="success" size="sm">
                                            <CheckCircle className="size-3 mr-0.5" />
                                            Resolved
                                        </Badge>
                                    ) : (
                                        <Badge
                                            color={severityConfig?.badgeColor ?? 'gray'}
                                            size="sm"
                                        >
                                            {issue.severity}
                                        </Badge>
                                    )}
                                    <span className="flex items-center gap-1 text-xs text-quaternary shrink-0">
                                        <Clock className="size-3" />
                                        {getRelativeTime(issue.created_at)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
